import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  TextField,
  Button,
  IconButton,
  LinearProgress,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CameraAlt as CameraIcon,
  Note as NoteIcon,
  SkipNext as SkipIcon,
  CheckCircle as DoneIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Preview as PreviewIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { getStocktakeDetail, updateStocktakeItem, skipStocktakeItem, transitionStocktake } from '../../collections/stocktakes';
import StocktakeItem from '../../models/stocktakeItem';
import ProductNavigationDialog from './ProductNavigationDialog';

const StocktakeCountingScreen = (props) => {
  const { stocktakeId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [stocktake, setStocktake] = useState(null);
  const [items, setItems] = useState([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [countInput, setCountInput] = useState('');
  const [spoilageInput, setSpoilageInput] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Dialog states
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [navigationDialogOpen, setNavigationDialogOpen] = useState(false);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);

  useEffect(() => {
    props.setPageTitle("Kiểm kho sản phẩm");
    fetchStocktakeData();
    
    // Cleanup on unmount
  }, [stocktakeId]);

  // Load current item data when index changes
  useEffect(() => {
    if (items.length > 0 && currentItemIndex < items.length) {
      const currentItem = items[currentItemIndex];
      console.log('Loading item data for index', currentItemIndex, ':', {
        is_counted: currentItem.is_counted,
        physical_count: currentItem.physical_count,
        spoilage_count: currentItem.spoilage_count,
        notes: currentItem.notes
      });
      
      // Load values regardless of counted status - user might want to edit
      setCountInput(currentItem.physical_count ? String(currentItem.physical_count) : '');
      setSpoilageInput(currentItem.spoilage_count ? String(currentItem.spoilage_count) : '');
      setNotesInput(currentItem.notes || '');
      setHasUnsavedChanges(false);
    }
  }, [currentItemIndex, items]);

  const fetchStocktakeData = async () => {
    console.log('Fetching stocktake data for ID:', stocktakeId);
    
    try {
      setLoading(true);
      const response = await getStocktakeDetail(stocktakeId);
      console.log('Stocktake detail response:', response);
      
      if (response && response.status === 200) {
        const stocktakeData = response.data;
        console.log('Stocktake data:', stocktakeData);
        setStocktake(stocktakeData);
        
        // Map items to StocktakeItem models
        const stocktakeItems = (stocktakeData.items || []).map(item => {
          const stocktakeItem = new StocktakeItem();
          stocktakeItem.mapData(item);
          return stocktakeItem;
        });
        
        console.log('Mapped stocktake items:', stocktakeItems);
        setItems(stocktakeItems);
        
        // Find first uncounted item or start from beginning
        const firstUncountedIndex = stocktakeItems.findIndex(item => !item.is_counted && !item.is_skipped);
        setCurrentItemIndex(firstUncountedIndex >= 0 ? firstUncountedIndex : 0);
        console.log('First uncounted index:', firstUncountedIndex);
      } else {
        console.error('Unexpected response format:', response);
        toast.error('Lỗi tải dữ liệu - phản hồi không đúng định dạng');
      }
    } catch (error) {
      console.error('Error fetching stocktake:', error);
      toast.error('Lỗi tải dữ liệu kiểm kho');
    } finally {
      setLoading(false);
    }
  };

  // Check if current item has changes that need saving
  const checkForUnsavedChanges = useCallback(() => {
    if (items.length > 0 && currentItemIndex < items.length) {
      const currentItem = items[currentItemIndex];
      
      // Normalize values for comparison
      const currentCount = currentItem.physical_count ? String(currentItem.physical_count) : '';
      const currentSpoilage = currentItem.spoilage_count ? String(currentItem.spoilage_count) : '';
      const currentNotes = currentItem.notes || '';
      
      const hasChanges = (
        countInput !== currentCount ||
        spoilageInput !== currentSpoilage ||
        notesInput !== currentNotes
      );
      setHasUnsavedChanges(hasChanges);
    }
  }, [items, currentItemIndex, countInput, spoilageInput, notesInput]);

  const saveItemCount = async (itemData, showToast = true) => {
    if (!itemData || saving) return false;
    
    try {
      setSaving(true);
      
      // API call to save item count using proper API helper
      const response = await updateStocktakeItem(stocktakeId, itemData.id, {
        physical_count: parseFloat(itemData.physical_count),
        spoilage_count: parseFloat(itemData.spoilage_count || 0),
        notes: itemData.notes
      });

      if (response && response.status === 200) {
        const result = response;
        
        // Update local state
        const updatedItems = [...items];
        const updatedItem = new StocktakeItem();
        updatedItem.mapData(result.data);
        updatedItems[currentItemIndex] = updatedItem;
        setItems(updatedItems);
        setHasUnsavedChanges(false);
        
        if (showToast) {
          toast.success('Đã lưu thành công');
        }
        return true;
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error('Error saving count:', error);
      if (showToast) {
        toast.error('Lỗi lưu dữ liệu');
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleCountChange = (value) => {
    setCountInput(value);
  };
  
  // Check for unsaved changes when inputs change
  useEffect(() => {
    checkForUnsavedChanges();
  }, [checkForUnsavedChanges]);

  const handleSpoilageChange = (value) => {
    setSpoilageInput(value);
  };

  const handleNotesChange = (value) => {
    setNotesInput(value);
  };

  const submitAndMoveNext = async () => {
    // Validate that count is entered and is a valid number
    if (!countInput || countInput.trim() === '') {
      toast.error('Vui lòng nhập số lượng thực tế');
      return;
    }
    
    if (isNaN(parseFloat(countInput)) || parseFloat(countInput) < 0) {
      toast.error('Số lượng phải là số hợp lệ và không âm');
      return;
    }

    // Save current item
    const currentItem = items[currentItemIndex];
    const itemData = {
      ...currentItem,
      physical_count: countInput,
      spoilage_count: spoilageInput || 0,
      notes: notesInput
    };

    const success = await saveItemCount(itemData, true);
    if (success) {
      if (currentItemIndex < items.length - 1) {
        setCurrentItemIndex(currentItemIndex + 1);
      } else {
        // Last item - show completion dialog
        setCompletionDialogOpen(true);
      }
    }
  };

  const navigateToItem = (direction) => {
    if (direction === 'next') {
      submitAndMoveNext();
    } else if (direction === 'prev' && currentItemIndex > 0) {
      // Allow going back without saving - just warn if there are unsaved changes
      if (hasUnsavedChanges) {
        toast.warning('Thay đổi chưa được lưu sẽ bị mất');
      }
      setCurrentItemIndex(currentItemIndex - 1);
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      setSaving(true);
      const response = await transitionStocktake(stocktakeId, 'submit');
      if (response && response.status === 200) {
        toast.success('Đã gửi phiếu kiểm kho để duyệt');
        navigate('/stocktakes');
      }
    } catch (error) {
      console.error('Error submitting stocktake:', error);
      toast.error(error.response?.data?.error || 'Lỗi gửi duyệt phiếu kiểm kho');
    } finally {
      setSaving(false);
    }
  };

  const handleGoToReview = () => {
    navigate(`/stocktakes/${stocktakeId}`);
  };

  const handleContinueCounting = () => {
    setCompletionDialogOpen(false);
    // Find first uncounted item
    const uncountedIndex = items.findIndex(item => !item.is_counted && !item.is_skipped);
    if (uncountedIndex >= 0) {
      setCurrentItemIndex(uncountedIndex);
    } else {
      setCurrentItemIndex(0);
    }
  };

  const handleSkipItem = async () => {
    if (!skipReason.trim()) {
      toast.error('Vui lòng nhập lý do bỏ qua');
      return;
    }

    try {
      setSaving(true);
      const currentItem = items[currentItemIndex];
      
      const response = await skipStocktakeItem(stocktakeId, currentItem.id, {
        reason: skipReason
      });

      if (response && response.status === 200) {
        const result = response;
        
        // Update local state
        const updatedItems = [...items];
        const updatedItem = new StocktakeItem();
        updatedItem.mapData(result.data);
        updatedItems[currentItemIndex] = updatedItem;
        setItems(updatedItems);
        
        toast.success('Đã bỏ qua sản phẩm');
        setSkipDialogOpen(false);
        setSkipReason('');
        
        // Move to next uncounted item
        const nextUncountedIndex = items.findIndex((item, index) => 
          index > currentItemIndex && !item.is_counted && !item.is_skipped
        );
        if (nextUncountedIndex >= 0) {
          setCurrentItemIndex(nextUncountedIndex);
        }
      }
    } catch (error) {
      console.error('Error skipping item:', error);
      toast.error('Lỗi bỏ qua sản phẩm');
    } finally {
      setSaving(false);
    }
  };

  console.log('Component render - loading:', loading, 'items length:', items.length, 'stocktake:', stocktake);
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Đang tải dữ liệu kiểm kho...</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Stocktake ID: {stocktakeId}
        </Typography>
      </Container>
    );
  }

  if (!stocktake && !loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 2 }}>
        <Alert severity="error">
          Không tìm thấy phiếu kiểm kho với ID: {stocktakeId}
        </Alert>
        <Button onClick={() => navigate('/stocktakes')} sx={{ mt: 2 }}>
          Quay lại danh sách
        </Button>
      </Container>
    );
  }

  if (!items.length && !loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 2 }}>
        <Alert severity="info">
          Không có sản phẩm nào cần kiểm trong phiếu này. Phiếu kiểm kho đã hoàn thành.
        </Alert>
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleSubmitForApproval}
            disabled={saving}
          >
            Gửi duyệt
          </Button>
          <Button 
            variant="outlined"
            onClick={() => navigate(`/stocktakes/${stocktakeId}`)}
          >
            Xem chi tiết
          </Button>
          <Button 
            variant="outlined"
            onClick={() => navigate('/stocktakes')}
          >
            Quay lại danh sách
          </Button>
        </Box>
      </Container>
    );
  }

  const currentItem = items[currentItemIndex];
  const progress = Math.round(((currentItemIndex + 1) / items.length) * 100);
  const countedItems = items.filter(item => item.is_counted).length;
  const skippedItems = items.filter(item => item.is_skipped).length;

  // Calculate variance for current input
  let currentVariance = null;
  let varianceDisplay = null;
  if (countInput && !isNaN(parseFloat(countInput))) {
    currentVariance = parseFloat(countInput) - parseFloat(currentItem.expected_quantity || 0);
    const percentage = currentItem.expected_quantity ? 
      Math.round((currentVariance / currentItem.expected_quantity) * 100) : 0;
    
    let color = 'success.main';
    if (Math.abs(percentage) > 20) color = 'error.main';
    else if (Math.abs(percentage) > 10) color = 'warning.main';
    else if (currentVariance !== 0) color = 'info.main';
    
    varianceDisplay = {
      text: `${currentVariance >= 0 ? '+' : ''}${currentVariance.toFixed(2)} (${percentage}%)`,
      color
    };
  }

  return (
    <Container maxWidth="md" sx={{ py: 1 }}>
      {/* Progress Header */}
      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" component="h1">
            Sản phẩm {currentItemIndex + 1}/{items.length}
          </Typography>
          <Chip 
            label={`${progress}% hoàn thành`}
            color="primary"
            size="small"
          />
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progress}
          sx={{ height: 8, borderRadius: 4, mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary">
          Đã kiểm: {countedItems} • Bỏ qua: {skippedItems} • Còn lại: {items.length - countedItems - skippedItems}
        </Typography>
      </Box>

      {/* Product Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Product Image */}
          {currentItem.product?.image && (
            <CardMedia
              component="img"
              height="200"
              image={currentItem.product.image}
              alt={currentItem.product.name}
              sx={{ borderRadius: 1, mb: 2 }}
            />
          )}

          {/* Product Name */}
          <Typography variant="h5" component="h2" gutterBottom>
            {currentItem.getProductDisplayName()}
          </Typography>

          {/* Expected Quantity */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="h6" color="primary">
              Số lượng dự kiến: {currentItem.getFormattedExpectedQuantity()}
            </Typography>
          </Box>

          {/* Count Input */}
          <TextField
            fullWidth
            label="Số lượng thực tế"
            type="number"
            value={countInput}
            onChange={(e) => handleCountChange(e.target.value)}
            inputProps={{ 
              min: 0, 
              step: 0.01,
              style: { fontSize: '1.5rem', textAlign: 'center' }
            }}
            sx={{ mb: 2 }}
            autoFocus
          />

          {/* Spoilage Input */}
          <TextField
            fullWidth
            label="Hư hỏng/hao hụt"
            type="number"
            value={spoilageInput}
            onChange={(e) => handleSpoilageChange(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mb: 2 }}
            helperText="Số lượng sản phẩm bị hư hỏng hoặc hao hụt"
          />

          {/* Variance Display */}
          {varianceDisplay && (
            <Alert 
              severity={
                currentVariance === 0 ? 'success' :
                Math.abs(currentVariance) > 20 ? 'error' :
                Math.abs(currentVariance) > 10 ? 'warning' : 'info'
              }
              sx={{ mb: 2 }}
            >
              <Typography variant="body1">
                Chênh lệch: {varianceDisplay.text}
              </Typography>
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<NoteIcon />}
              onClick={() => setNotesDialogOpen(true)}
              size="small"
            >
              Ghi chú
            </Button>
            <Button
              variant="outlined"
              startIcon={<CameraIcon />}
              size="small"
            >
              Chụp ảnh
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<SkipIcon />}
              onClick={() => setSkipDialogOpen(true)}
              size="small"
            >
              Bỏ qua
            </Button>
          </Box>

          {/* Save Status */}
          {saving && (
            <Typography variant="body2" color="text.secondary">
              Đang lưu...
            </Typography>
          )}
          {hasUnsavedChanges && !saving && (
            <Typography variant="body2" color="warning.main">
              Có thay đổi chưa được lưu
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigateToItem('prev')}
          disabled={currentItemIndex === 0 || saving}
        >
          Trước
        </Button>
        
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigateToItem('next')}
          disabled={saving || !countInput || countInput.trim() === '' || isNaN(parseFloat(countInput)) || parseFloat(countInput) < 0}
          color="primary"
        >
          {currentItemIndex === items.length - 1 ? 'Hoàn thành' : (
            items[currentItemIndex]?.is_counted ? 'Cập nhật & Tiếp theo' : 'Lưu & Tiếp theo'
          )}
        </Button>
      </Box>

      {/* Floating Action Buttons */}
      <Box sx={{ position: 'fixed', bottom: 16, right: 16 }}>
        <Fab
          color="primary"
          onClick={() => setNavigationDialogOpen(true)}
          sx={{ mr: 1 }}
        >
          <SearchIcon />
        </Fab>
      </Box>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Thêm ghi chú</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Ghi chú về sản phẩm này"
            value={notesInput}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Ví dụ: Sản phẩm hơi úng, một số bị dập..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)}>Hủy</Button>
          <Button onClick={() => setNotesDialogOpen(false)} variant="contained">Lưu</Button>
        </DialogActions>
      </Dialog>

      {/* Skip Dialog */}
      <Dialog open={skipDialogOpen} onClose={() => setSkipDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bỏ qua sản phẩm</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Lý do bỏ qua"
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            placeholder="Ví dụ: Không tìm thấy trong kho, sản phẩm đã hết..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleSkipItem} variant="contained" color="warning">
            Bỏ qua
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Navigation Dialog */}
      <ProductNavigationDialog
        open={navigationDialogOpen}
        onClose={() => setNavigationDialogOpen(false)}
        items={items}
        currentIndex={currentItemIndex}
        onSelectItem={(index) => setCurrentItemIndex(index)}
      />

      {/* Completion Dialog */}
      <Dialog
        open={completionDialogOpen}
        onClose={() => setCompletionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DoneIcon color="success" />
            Hoàn thành kiểm kho
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Bạn đã hoàn thành kiểm kê tất cả sản phẩm!
          </Typography>
          
          {/* Summary Stats */}
          <Paper elevation={0} sx={{ p: 2, my: 2, bgcolor: 'grey.100' }}>
            <Typography variant="subtitle2" gutterBottom>
              Tóm tắt kiểm kho:
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                • Tổng sản phẩm: {items.length}
              </Typography>
              <Typography variant="body2">
                • Đã kiểm: {items.filter(item => item.is_counted).length}
              </Typography>
              <Typography variant="body2">
                • Bỏ qua: {items.filter(item => item.is_skipped).length}
              </Typography>
              <Typography variant="body2">
                • Chênh lệch: {items.filter(item => item.is_counted && item.variance !== 0).length} sản phẩm
              </Typography>
            </Box>
          </Paper>

          <Typography variant="body2" color="text.secondary">
            Bạn có thể:
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={handleSubmitForApproval}
            disabled={saving}
          >
            Gửi duyệt
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={handleGoToReview}
          >
            Xem chi tiết & Kiểm tra lại
          </Button>
          <Button
            fullWidth
            variant="text"
            startIcon={<EditIcon />}
            onClick={handleContinueCounting}
          >
            Tiếp tục kiểm kho
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StocktakeCountingScreen;